import { type DispatcherContext, EVENT_ROUTING, type PlatformConfig, type PlatformEntry } from '../config/eventRouting';
import type { AnalyticsEventMap, AnalyticsEventName } from '../types/events';
import { withSubscriptionService } from '../utils/paymentEventProps';

function dispatchEntry(
  entry: PlatformEntry,
  platformFn: (name: string, props?: Record<string, unknown>) => void,
  ctx: DispatcherContext,
  props: Record<string, unknown>,
): void {
  if (typeof entry === 'string') {
    platformFn(entry);
    return;
  }
  if (entry.filter && !entry.filter(props)) {
    return;
  }
  const name = typeof entry.name === 'function' ? entry.name(props) : entry.name;
  const resolvedProps = entry.resolve ? entry.resolve(ctx, props) : props;
  platformFn(name, resolvedProps);
}

function dispatchPlatform(
  config: PlatformConfig | undefined,
  platformFn: (name: string, props?: Record<string, unknown>) => void,
  ctx: DispatcherContext,
  props: Record<string, unknown>,
): void {
  if (!config) return;
  const entries = Array.isArray(config) ? config : [config];
  for (const entry of entries) {
    dispatchEntry(entry, platformFn, ctx, props);
  }
}

export function useEventsDispatcher() {
  const { $versions, $ttTrack, $snapTrack, $taboolaPushEvent } = useNuxtApp();
  const { mixpanelQuizTrack, mixpanelTrack, setUserProperty } = useMixpanel();
  const { fbqTrack } = useFacebookPixel();
  const { track: redTrack } = useRedTrack();
  const { track: gbTrack } = useGrowthBookTrack();
  const { sendGtmPurchase } = useCustomGtag();
  const store = useKegelAnalyticStore();
  const gatewayStore = useKegelGatewayStore();
  const { locale } = useI18n();
  const configsStore = usePaymentConfigs(locale);
  const route = useRoute();
  const { experimentId, variation } = useVersions();
  const { getClickID } = useClickID();

  function buildContext(): DispatcherContext {
    return {
      sha256UserEmail: store.sha256UserEmail,
      userEmail: store.userEmail,
      get currencyCode() {
        return configsStore.currentConfig[1]?.currencyCode ?? 'USD';
      },
      clickId: (route.query.rtkcid as string) || store.clickId,
    };
  }

  function track<E extends AnalyticsEventName>(event: E, props?: AnalyticsEventMap[E]): void {
    const config = EVENT_ROUTING[event];
    const rawProps = withSubscriptionService(
      event,
      (props ?? {}) as Record<string, unknown>,
      gatewayStore.subscriptionService,
    );
    const ctx = buildContext();

    if (!config.noMixpanel) {
      if (config.suppressible) {
        mixpanelQuizTrack(event, rawProps);
      } else {
        mixpanelTrack(event, rawProps);
      }
    }

    dispatchPlatform(config.facebook, name => fbqTrack(name), ctx, rawProps);
    dispatchPlatform(config.redtrack, name => redTrack(name), ctx, rawProps);
    dispatchPlatform(config.tiktok, name => ($ttTrack as any)?.(name), ctx, rawProps);
    dispatchPlatform(config.snap, (name, p) => ($snapTrack as any)?.(name, p), ctx, rawProps);
    dispatchPlatform(config.growthbook, (name, p) => gbTrack(name, p ?? {}), ctx, rawProps);
    dispatchPlatform(config.taboola, name => ($taboolaPushEvent as any)?.(name), ctx, rawProps);

    if (config.gtag) {
      sendGtmPurchase(rawProps.price as number);
    }
  }

  // --- named methods for cases with guards or conditional logic ---

  function trackStartScreenShown(): void {
    if (!import.meta.client || !route.meta?.isStartPage) return;

    let isBeaconSent = Boolean((globalThis as any).__aoStartScreenBeaconSent);
    if (!isBeaconSent) {
      try {
        isBeaconSent = sessionStorage.getItem('aoStartScreenBeaconSent') === '1';
      } catch {
        isBeaconSent = false;
      }
    }

    if (isBeaconSent) {
      fbqTrack('PageView');
      ($ttTrack as any)?.('ViewContent');
      ($taboolaPushEvent as any)?.('page_view');
      return;
    }

    track('Start Screen Shown', {
      Quiz_version: $versions?.value.quiz,
      Version: $versions?.value.homePage,
      Landing_version: $versions?.value.landing,
      Locale: locale.value?.toUpperCase(),
      Funnel: $versions?.value.funnel || '',
    });
  }

  async function trackQuizStarted(ageGroupLabel?: string): Promise<void> {
    if (!import.meta.client) return;

    if (!store.clickId) {
      await getClickID(experimentId, variation);
    }

    if (ageGroupLabel) {
      setUserProperty({ 'Age Group': ageGroupLabel });
    }

    track('Quiz Started');
  }

  function trackLpShown(isSuperDiscount = false, isSendSideEvents = false): void {
    track('Landing Page Shown', {
      Type: isSuperDiscount ? 'discount' : 'regular',
      version: $versions?.value.landing,
    });

    if (isSendSideEvents) {
      fbqTrack('PageView');
      ($ttTrack as any)?.('ViewContent', { content_type: 'product' });
    }
  }

  return {
    track,
    trackStartScreenShown,
    trackQuizStarted,
    trackLpShown,
  };
}
